/**
 * TYPO3 Entry Point for LocationFinder
 * This file initializes the React LocationFinder for TYPO3 integration
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { LocationFinder } from './LocationFinder';
import { ZOOM_DEFAULTS } from './LocationFinderTypes';
import './locationfinder.css';

import { onDomReady } from '../../assets/TypeScript/OnDomReady';

// Extend window interface for initLocationFinder
declare global {
    interface Window {
        initLocationFinder: () => void;
    }
}

// Self-initializing bundle for TYPO3
(function () {
    'use strict';

    function initLocationFinder() {
        const elements = document.querySelectorAll('[data-locationfinder]');

        elements.forEach((element) => {
            // Skip if already initialized
            if (element.hasAttribute('data-locationfinder-initialized')) {
                return;
            }

            element.setAttribute('data-locationfinder-initialized', 'true');

            // Parse configuration from data attributes
            const config = {
                apiEndpoint: element.getAttribute('data-api-endpoint') || '',
                instanceId: element.getAttribute('data-instance-id') || undefined,
                searchRadiusOptions: (element.getAttribute('data-search-radius-options') || '')
                    .split(',')
                    .map((value) => parseInt(value.trim(), 10))
                    .filter((value) => Number.isFinite(value) && value > 0),
                language: element.getAttribute('data-language') || 'de',
                preselectedCompany: element.getAttribute('data-preselected-company') || '',
                preselectedArea: element.getAttribute('data-preselected-area') || '',
                defaultRadius: element.getAttribute('data-default-radius') || '10',
                enableAreaFilter: element.getAttribute('data-enable-area-filter') === '1',
                defaultCenterLat: parseFloat(element.getAttribute('data-default-center-lat') || '52.42068587567314'),
                defaultCenterLng: parseFloat(element.getAttribute('data-default-center-lng') || '9.755485399936074'),
                limitNearestLocations: parseInt(element.getAttribute('data-limit-nearest-locations') || '10', 10),
                googleMapsApiKey: element.getAttribute('data-google-maps-api-key') || '',
                topline: element.getAttribute('data-topline')?.trim() || undefined,
                headline: element.getAttribute('data-headline')?.trim() || undefined,
                description: element.getAttribute('data-description')?.trim() || undefined,
                useZoomlevel: element.getAttribute('data-use-zoomlevel') === '1',
                usePrioritySort: element.getAttribute('data-use-priority-sort') === '1',
                zoomSingleMarker: parseInt(element.getAttribute('data-zoom-single-marker') || String(ZOOM_DEFAULTS.SINGLE_MARKER), 10),
                zoomSelectedLocation: parseInt(element.getAttribute('data-zoom-selected-location') || String(ZOOM_DEFAULTS.SELECTED_LOCATION), 10),
                zoomInitial: parseInt(element.getAttribute('data-zoom-initial') || String(ZOOM_DEFAULTS.INITIAL), 10),
            };

            // Parse i18n translations from individual data attributes
            const i18n: {
                topline?: string;
                headline?: string;
                description?: string;
                [key: string]: string | undefined;
            } = {
                topline: element.getAttribute('data-i18n-topline')?.trim() || undefined,
                headline: element.getAttribute('data-i18n-headline')?.trim() || undefined,
                description: element.getAttribute('data-i18n-description')?.trim() || undefined,
                searchPlaceholder: element.getAttribute('data-i18n-search-placeholder') || undefined,
                searchLabel: element.getAttribute('data-i18n-search-label') || undefined,
                filterShow: element.getAttribute('data-i18n-filter-show') || undefined,
                filterHide: element.getAttribute('data-i18n-filter-hide') || undefined,
                filterReset: element.getAttribute('data-i18n-filter-reset') || undefined,
                radiusLabel: element.getAttribute('data-i18n-radius-label') || undefined,
                companyLabel: element.getAttribute('data-i18n-company-label') || undefined,
                areaLabel: element.getAttribute('data-i18n-area-label') || undefined,
                selectPlaceholder: element.getAttribute('data-i18n-select-placeholder') || undefined,
                resultsFound: element.getAttribute('data-i18n-results-found') || undefined,
                noResults: element.getAttribute('data-i18n-no-results') || undefined,
                loading: element.getAttribute('data-i18n-loading') || undefined,
                searchButton: element.getAttribute('data-i18n-search-button') || undefined,
                showOnMap: element.getAttribute('data-i18n-show-on-map') || undefined,
                details: element.getAttribute('data-i18n-details') || undefined,
                useMyLocation: element.getAttribute('data-i18n-use-my-location') || undefined,
                locationNotSupported: element.getAttribute('data-i18n-location-not-supported') || undefined,
                locationError: element.getAttribute('data-i18n-location-error') || undefined,
                locationDenied: element.getAttribute('data-i18n-location-denied') || undefined,
                locationUnavailable: element.getAttribute('data-i18n-location-unavailable') || undefined,
                locationTimeout: element.getAttribute('data-i18n-location-timeout') || undefined,
                selectAll: element.getAttribute('data-i18n-select-all') || undefined,
                radiusOption10: element.getAttribute('data-i18n-radius-option10') || undefined,
                radiusOption25: element.getAttribute('data-i18n-radius-option25') || undefined,
                radiusOption50: element.getAttribute('data-i18n-radius-option50') || undefined,
                radiusOption100: element.getAttribute('data-i18n-radius-option100') || undefined,
                radiusOption150: element.getAttribute('data-i18n-radius-option150') || undefined,
                listView: element.getAttribute('data-i18n-list-view') || undefined,
                mapView: element.getAttribute('data-i18n-map-view') || undefined,
                overlayText: element.getAttribute('data-overlay-text') || undefined,
                overlayLabel: element.getAttribute('data-overlay-label') || undefined,
            };

            // Create container
            const container = document.createElement('div');
            container.className = 'locationfinder-root';
            element.appendChild(container);

            // Render React component
            const root = ReactDOM.createRoot(container);
            root.render(
                <React.StrictMode>
                    <LocationFinder
                        apiEndpoint={config.apiEndpoint}
                        instanceId={config.instanceId}
                        searchRadiusOptions={config.searchRadiusOptions}
                        preselectedCompany={config.preselectedCompany}
                        preselectedArea={config.preselectedArea}
                        defaultRadius={config.defaultRadius}
                        enableAreaFilter={config.enableAreaFilter}
                        defaultCenterLat={config.defaultCenterLat}
                        defaultCenterLng={config.defaultCenterLng}
                        limitNearestLocations={config.limitNearestLocations}
                        googleMapsApiKey={config.googleMapsApiKey}
                        topline={config.topline}
                        headline={config.headline}
                        description={config.description}
                        i18n={i18n}
                        useZoomlevel={config.useZoomlevel}
                        usePrioritySort={config.usePrioritySort}
                        zoomSingleMarker={config.zoomSingleMarker}
                        zoomSelectedLocation={config.zoomSelectedLocation}
                        zoomInitial={config.zoomInitial}
                    />
                </React.StrictMode>
            );
        });
    }

    // Initialize on DOM ready
    onDomReady(initLocationFinder);

    // Re-check after delay for dynamic content
    setTimeout(initLocationFinder, 100);

    // Expose for manual init if needed
    window.initLocationFinder = initLocationFinder;
})();
